import { type ButtonHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

type Variant =
  | 'primary' | 'soft-primary' | 'light' | 'ghost'
  | 'danger' | 'soft-danger' | 'soft-success' | 'soft-info'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'sm' | 'md'
  icon?: string
  loading?: boolean
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-gradient-primary text-white hover:brightness-110 active:brightness-95',
  'soft-primary': 'bg-primary/10 text-primary hover:bg-primary hover:text-bg',
  light: 'bg-surface2 text-white hover:bg-surface3',
  ghost: 'text-muted hover:text-white hover:bg-surface2',
  danger: 'bg-expense text-white hover:bg-expense/90',
  'soft-danger': 'bg-expense/10 text-expense hover:bg-expense hover:text-white',
  'soft-success': 'bg-income/10 text-income hover:bg-income hover:text-white',
  'soft-info': 'bg-info/10 text-info hover:bg-info hover:text-white',
}

const SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-[38px] px-4 text-13 gap-2',
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading,
  disabled,
  children,
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded transition-colors whitespace-nowrap',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Spinner /> : icon && <i className={cn(icon, size === 'sm' ? 'text-sm' : 'text-base', 'leading-none')} aria-hidden />}
      {children}
    </button>
  )
}

type IconTone = 'primary' | 'info' | 'danger' | 'success' | 'muted'

const ICON_TONES: Record<IconTone, string> = {
  primary: 'text-primary hover:bg-primary/10',
  info: 'text-info hover:bg-info/10',
  danger: 'text-expense hover:bg-expense/10',
  success: 'text-income hover:bg-income/10',
  muted: 'text-muted hover:text-white hover:bg-surface2',
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string
  label: string
  tone?: IconTone
}

export function IconButton({ icon, label, tone = 'muted', className, type = 'button', ...props }: IconButtonProps) {
  return (
    <button
      type={type}
      title={label}
      aria-label={label}
      className={cn('inline-flex h-8 w-8 items-center justify-center rounded transition-colors disabled:opacity-40', ICON_TONES[tone], className)}
      {...props}
    >
      <i className={cn(icon, 'text-base leading-none')} aria-hidden />
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent', className)}
      role="status"
      aria-label="Carregando"
    />
  )
}
